/**
 * The invoices tab: the list of invoices, or one invoice's page.
 *
 * Which of the two is showing is URL state (`?doc=<id>`), read on every render,
 * the same rule the projects tab follows for `?project=`. An invoice used to
 * open in a dialog held in component state, so it could not be linked to,
 * reloaded into or left with the browser's Back button. As a page it gets all
 * three, and the width a four-column table needs.
 *
 * The parameter is `doc` and not `invoice` on purpose: the card payment's
 * return address already uses `?invoice=` for the invoice NUMBER, and one name
 * carrying an id on the way in and a number on the way back would open the
 * wrong thing exactly when a client has just paid.
 *
 * Paying by card goes through the CRM's own Stripe account. The button is only
 * drawn when the server says so (`portal.invoicePayable`), and it never marks
 * anything paid: Stripe sends the client back here and the money is written by
 * the webhook, so the return banner says the payment is being confirmed rather
 * than that it is done. Anything else would show a client an invoice marked
 * settled that our books do not agree with yet.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CircleCheck, Info } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { keepPreview } from "../../../lib/previewMode";
import { Notice } from "../../ui";
import { InvoiceList } from "./InvoiceList";
import { InvoicePage } from "./InvoicePage";

export function InvoicesSection() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const docId = params.get("doc");
  // Where Stripe sends the client back to. Read once, from the address the
  // browser arrived on, and then cleared - a reload should not replay the banner.
  const [payReturn, setPayReturn] = useState<"success" | "cancel" | null>(null);

  useEffect(() => {
    const state = params.get("pay");
    if (state !== "success" && state !== "cancel") return;
    setPayReturn(state);
    // Through the router rather than `history.replaceState`, so the next read
    // of the query string agrees with the address bar. Everything else in it
    // stays - including a preview token.
    const next = new URLSearchParams(params);
    next.delete("pay");
    next.delete("invoice");
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Every rewrite goes through `keepPreview`: `setParams` replaces the whole
  // query string, and a staff preview would otherwise drop its token.
  const open = (id: string) => setParams(keepPreview({ tab: "invoices", doc: id }));
  const back = () => setParams(keepPreview({ tab: "invoices" }));

  return (
    <div className="space-y-4">
      {/* The success wording is deliberately "being confirmed" and not "paid".
          Only the webhook may write money, and it can arrive seconds after the
          redirect - telling a client an invoice is settled while our books still
          say otherwise is worse than a moment of ambiguity. */}
      {payReturn === "success" && (
        <Notice icon={CircleCheck} tone="success">{t("portal.invoices.payPending")}</Notice>
      )}
      {payReturn === "cancel" && (
        <Notice icon={Info} tone="neutral">{t("portal.invoices.payCancelled")}</Notice>
      )}

      {docId ? (
        // Keyed by id so opening another invoice remounts rather than showing
        // the previous one's lines under the new number for a frame.
        <InvoicePage key={docId} id={docId} onBack={back} />
      ) : (
        <InvoiceList onOpen={open} />
      )}
    </div>
  );
}
